<template>
  <div class="match-grid">
    <p v-if="matches.length === 0" class="grid-empty">
      Für diese Saison sind keine Spiele erfasst.
    </p>

    <template v-else>
      <div class="grid-tiles">
        <button v-for="(match, index) in matches" :key="match.date" type="button" class="grid-tile"
                :class="{ 'grid-tile-active': selectedIndex === index }"
                :style="{ background: moodColor(match.score) }"
                :title="tileTitle(match)"
                @click="select(index)">
          <span class="tile-day">{{ matchdayLabel(match) }}</span>
          <img class="tile-logo" :src="match.evnt.image" :alt="''" loading="lazy" />
          <span class="tile-result">{{ match.evnt.text || '–' }}</span>
        </button>
      </div>

      <!-- Skalenlegende: bei einer Farbskala als einzigem Traeger des Werts nicht optional. -->
      <div class="grid-legend">
        <span class="legend-caption">Stimmung</span>
        <span v-for="step in scale" :key="step.label" class="legend-step">
          <span class="legend-swatch" :style="{ background: step.color }"></span>
          {{ step.label }}
        </span>
      </div>

      <div class="grid-detail" :class="{ 'grid-detail-empty': !selectedMatch }">
        <template v-if="selectedMatch">
          <img class="detail-logo" :src="selectedMatch.evnt.image" :alt="''" />
          <div class="detail-body">
            <div class="detail-title">
              {{ opponentName(selectedMatch.evnt) }}
              <span class="detail-venue">{{ selectedMatch.evnt.home === false ? 'auswärts' : 'heim' }}</span>
            </div>
            <div class="detail-meta">
              {{ selectedMatch.evnt.md }} · {{ formatDate(selectedMatch.date) }}
            </div>
          </div>
          <div class="detail-values">
            <span class="detail-result" :style="{ background: resultColor(selectedMatch.evnt) }">
              {{ selectedMatch.evnt.text || '–' }}
            </span>
            <span class="detail-score">{{ formatScore(selectedMatch.score) }}</span>
          </div>
        </template>
        <span v-else>Kachel antippen für Spieldetails</span>
      </div>
    </template>
  </div>
</template>

<script>
import * as utils from '../../utils.js';
import {
  MOOD_SCALE, formatDate, formatScore, getMatchEntries, moodColor, opponentLabel, resultLabel
} from './moodHistory.js';

/**
 * Variante B des Stimmungsverlaufs: ein Raster mit einer Kachel je Spiel, Kachelfarbe = Stimmung.
 *
 * Verzichtet bewusst auf die Zeitachse - dafuer passen alle ~38 Spiele auch auf ein Handydisplay,
 * ohne dass sich etwas ueberlagert. Das Raster fliesst ueber auto-fill, von 4 Spalten auf schmalen
 * Geraeten bis zu 12 auf grossen.
 */
export default {
  name: 'MoodMatchGrid',
  props: {
    season: { type: Object, required: true }
  },
  data() {
    return { selectedIndex: null };
  },
  computed: {
    matches() {
      return getMatchEntries(this.season);
    },
    selectedMatch() {
      return this.selectedIndex === null ? null : this.matches[this.selectedIndex];
    },
    scale() {
      return MOOD_SCALE;
    }
  },
  watch: {
    // Saisonwechsel: die Auswahl zeigt sonst auf ein Spiel der vorherigen Saison.
    season() {
      this.selectedIndex = null;
    }
  },
  methods: {
    formatScore,
    formatDate,
    moodColor,
    select(index) {
      this.selectedIndex = this.selectedIndex === index ? null : index;
    },
    resultColor(evnt) {
      return utils.getColorForEvent(evnt);
    },
    opponentName(evnt) {
      return evnt.opp || opponentLabel(evnt) || 'Gegner unbekannt';
    },
    matchdayLabel(match) {
      if (match.matchday !== null) return match.matchday;
      return match.evnt.md && match.evnt.md.startsWith('Pokal') ? 'DFB' : '·';
    },
    tileTitle(match) {
      return `${match.evnt.md} · ${this.opponentName(match.evnt)} · `
        + `${match.evnt.text || 'ohne Ergebnis'} (${resultLabel(match.evnt)}) · `
        + `Stimmung ${formatScore(match.score)}`;
    }
  }
};
</script>

<style scoped>
.match-grid {
  background: var(--chart-surface);
  border-radius: var(--radius-md);
  padding: var(--spacing-md);
}

.grid-empty {
  margin: 0;
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--chart-ink-mute);
}

.grid-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(58px, 1fr));
  gap: 6px;
}

.grid-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 5px 2px 4px;
  border: none;
  /* 2px Abstand zur Flaeche statt einer Umrandung - so trennen sich die Kacheln,
     ohne dass ein zusaetzlicher Strich ins Bild kommt. */
  outline: 2px solid var(--chart-surface);
  outline-offset: -2px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: transform 0.15s ease;
}

.grid-tile:hover {
  transform: translateY(-2px);
}

.grid-tile-active {
  outline: 2px solid var(--text-primary);
  outline-offset: -2px;
}

.grid-tile:focus-visible {
  outline: 2px solid var(--text-primary);
  outline-offset: 1px;
}

/* .tile-day/.tile-result bewusst ausserhalb der Font-Size-Skala (--fs-*): die Kachel ist auf
   minmax(58px, 1fr) fixiert, eine Vereinheitlichung auf --fs-xs waere +25% mit echtem
   Overflow-Risiko auf der engsten Flaeche der ganzen Ansicht - analog zur Mobile-Verdichtung in
   SeasonStatsTable.vue. */
.tile-day {
  font-size: 0.6rem;
  font-weight: 700;
  color: color-mix(in srgb, var(--chart-surface) 75%, transparent);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.tile-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.tile-result {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--chart-surface);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.grid-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-xs) var(--spacing-sm);
  margin-top: var(--spacing-md);
  color: var(--chart-ink-mute);
  font-size: var(--fs-sm);
}

.legend-caption {
  color: var(--text-secondary);
}

.legend-step {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-variant-numeric: tabular-nums;
}

.legend-swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.grid-detail {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-height: 58px;
  margin-top: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-top: 1px solid var(--chart-grid);
}

.grid-detail-empty {
  justify-content: center;
  color: var(--chart-ink-mute);
  font-size: var(--fs-sm);
}

.detail-logo {
  width: 34px;
  height: 34px;
  object-fit: contain;
  flex-shrink: 0;
}

.detail-body {
  flex: 1 1 auto;
  min-width: 0;
}

.detail-title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-venue {
  font-weight: 400;
  font-size: var(--fs-sm);
  color: var(--chart-ink-mute);
}

.detail-meta {
  font-size: var(--fs-sm);
  color: var(--chart-ink-mute);
}

.detail-values {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-shrink: 0;
}

.detail-result {
  color: var(--chart-surface);
  font-weight: 700;
  font-size: var(--fs-sm);
  border-radius: var(--radius-sm);
  padding: 2px 7px;
  font-variant-numeric: tabular-nums;
}

.detail-score {
  font-size: var(--fs-lg);
  font-weight: 600;
}
</style>
